import { useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/consultas-agenda" replace />;

  async function submit(e) {
    e.preventDefault();
    const email = emailRef.current?.value.trim() || "";
    const password = passwordRef.current?.value || "";
    if (!email || !password) return;
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Bem-vindo!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#EBE5DF]" data-testid="login-page">
      {/* LEFT — brand panel */}
      <div
        className="hidden md:flex flex-col justify-between text-[#EBE5DF] p-14 relative overflow-hidden"
        style={{
          backgroundColor: "#0B2230",
          backgroundImage:
            "linear-gradient(115deg, rgba(11,34,48,0.92) 0%, rgba(11,34,48,0.55) 45%, rgba(11,34,48,0.35) 100%), url('/brand/clinica-kloss-lobby.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        {/* Logo */}
        <div className="relative z-10">
          <img
            src="/brand/kloss-experience-wide.png"
            alt="KLOSS Experience"
            className="w-[380px] md:w-[440px] lg:w-[480px] h-auto select-none"
            draggable="false"
          />
        </div>

        {/* Tagline */}
        <div className="relative z-10 max-w-md">
          <h1 className="leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <span className="block text-white text-5xl font-medium tracking-tight">
              Gestão precisa.
            </span>
            <span
              className="block text-5xl font-medium tracking-tight italic mt-1"
              style={{ color: "#DBA470" }}
            >
              Cuidado em cada detalhe.
            </span>
          </h1>
          <p className="mt-6 text-[15px] leading-relaxed text-[#EBE5DF]/75 max-w-sm">
            Estoque, protocolos e aplicações integrados para uma rotina clínica
            mais segura e organizada.
          </p>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-[11px] uppercase tracking-[0.2em] text-[#EBE5DF]/40">
          © {new Date().getFullYear()} Clínica Kloss · Sistema interno · LGPD
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="flex items-center justify-center p-8 md:p-14 bg-[#EBE5DF]">
        <form
          onSubmit={submit}
          className="w-full max-w-[440px]"
          data-testid="login-form"
          autoComplete="on"
        >
          {/* Section label + accent line */}
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#636A59] font-medium">
              Área Restrita
            </div>
            <div className="mt-3 h-[2px] w-14 bg-[#DBA470]" />
          </div>

          {/* Heading */}
          <h2
            className="text-[#0B2230] text-4xl md:text-[42px] leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            Bem-vindo à Clínica Kloss
          </h2>
          <p className="mt-2 text-[15px] text-[#636A59]">
            Acesse o sistema de estoque e protocolos.
          </p>

          <div className="mt-10 space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="login-email" className="text-[11px] uppercase tracking-[0.22em] text-[#636A59] font-medium">
                E-mail
              </label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#636A59]/60" />
                <input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="Digite seu e-mail"
                  ref={emailRef}
                  className="w-full pl-11 pr-4 py-3.5 rounded-full border border-[#D6CFC5] bg-white/70 text-[#0B2230] placeholder:text-[#636A59]/50 focus:outline-none focus:border-[#DBA470] focus:bg-white transition"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="login-password" className="text-[11px] uppercase tracking-[0.22em] text-[#636A59] font-medium">
                Senha
              </label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#636A59]/60" />
                <input
                  id="login-password"
                  data-testid="login-password"
                  type={showPwd ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  placeholder="Digite sua senha"
                  ref={passwordRef}
                  className="w-full pl-11 pr-11 py-3.5 rounded-full border border-[#D6CFC5] bg-white/70 text-[#0B2230] placeholder:text-[#636A59]/50 focus:outline-none focus:border-[#DBA470] focus:bg-white transition"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#636A59]/70 hover:text-[#0B2230] transition"
                  aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="mt-8 w-full bg-[#0B2230] hover:bg-[#143346] disabled:opacity-60 text-white font-medium py-4 rounded-full uppercase tracking-[0.18em] text-[13px] flex items-center justify-center gap-2 transition shadow-[0_1px_0_#DBA470_inset,0_0_0_1px_#DBA470] hover:shadow-[0_1px_0_#DBA470_inset,0_0_0_1.5px_#DBA470]"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Entrando..." : "Entrar no sistema"}
          </button>

          {/* Forgot password */}
          <div className="text-center mt-6">
            <a
              href="/esqueci-senha"
              data-testid="forgot-password-link"
              className="text-[13px] text-[#636A59] hover:text-[#0B2230] border-b border-[#DBA470]/60 hover:border-[#DBA470] pb-0.5 transition"
            >
              Esqueci minha senha
            </a>
          </div>
          <div className="mt-8 border-t border-[#D6CFC5] pt-6 text-center">
            <p className="text-xs text-[#636A59]">Você é paciente?</p>
            <Link to="/area-paciente/login" data-testid="patient-portal-access-link" className="mt-3 inline-flex min-h-10 items-center justify-center rounded-full border border-[#0B2230] px-5 text-xs font-bold uppercase tracking-[.14em] text-[#0B2230] transition-colors hover:bg-[#0B2230] hover:text-white">Acessar Área do Paciente</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
